import React, { useEffect } from 'react';
import { get } from 'lodash';
import { useMatch } from 'react-router-dom';
import { useAtom, useAtomValue } from 'jotai';
import { useSelector, useDispatch } from 'react-redux';
import { summaryWidgetsSelector } from '@store/summaryWidgets/selector';
import { summaryPanelAtom, flowToolbarPanelAtom } from '@src/atoms/panels';
import { SUMMARY_PANEL_TYPE } from './constants';
import SPIWidgetPanel from './spi-panel';
import NetworkPanel from './app-network-panel';
import ErrorBoundary from '@components/common/ErrorBoundary';
import { intervalSelector } from '@store/common/selectors';
import { isAppModule, PRESET_DASHBOARD_ROUTE_PATH } from '@utils/constants';
import { multiDimensionDrilldown } from '@store/trends/base/actions';
import { setDrillDown } from '@store/networkRequest/actions';
import { compareOnTrendsModeSelector } from '@store/trends/base/selectors';
import { activeDashboardSelector } from '@src/store/tempDashboard/selectors';
import { getValidPresetKeys } from '@src/utils/presets/utils';
import { validateInterval as isValidateIntervalForTreeMap } from './app-network-panel/Panels/helpers';
import { isWidgetTabActiveAtom } from '@src/components/metrics-panel-hz/atoms';
import presetStore from '@src/globalStore/presetsStore';
import FlowPanel from './flow-panel';
import { useFeature } from '@src/globalStore/featureStore';
import { useDrilldownMeta } from '@src/hooks/common/useDrilldownMeta';
import featureNames from '@src/utils/featureToggle/constants';

const renderWidgetPanelComponent = ({ type, renderCondition, dispatch, flowId }) => {
  switch (type) {
    case SUMMARY_PANEL_TYPE.SPI:
      return renderCondition[SUMMARY_PANEL_TYPE.SPI] ? <SPIWidgetPanel /> : null;
    case SUMMARY_PANEL_TYPE.NETWORK:
      return renderCondition[SUMMARY_PANEL_TYPE.NETWORK] ? (
        <NetworkPanel
          onSelect={(payload) => {
            dispatch(multiDimensionDrilldown(payload));
            dispatch(setDrillDown(true));
          }}
          onResetCallback={() => dispatch(setDrillDown(false))}
        />
      ) : null;
    case SUMMARY_PANEL_TYPE.FLOW:
      return renderCondition[SUMMARY_PANEL_TYPE.FLOW] ? <FlowPanel flowId={flowId} /> : null;
    default:
      return null;
  }
};

const WidgetPanelContainer = () => {
  const dispatch = useDispatch();
  const flowToolbarPanel = useAtomValue(flowToolbarPanelAtom);
  const presetMatchedKey = useMatch(PRESET_DASHBOARD_ROUTE_PATH)?.params.id;
  const VSISummaryWidgets = useSelector(summaryWidgetsSelector);
  const { flowId } = useDrilldownMeta();
  const [showPanel, setSummaryPanel] = useAtom(summaryPanelAtom);
  const interval = useSelector(intervalSelector);
  const PRESET_KEYS = getValidPresetKeys();
  const activeDashboard = useSelector(activeDashboardSelector) as { usecaseKey?: string };
  const { usecaseKey } = activeDashboard || {};
  const enableTopBar = useFeature(featureNames.DPI_FLOW_TOP_BAR);
  let DPISummaryWidgets = useSelector(summaryWidgetsSelector);
  if (isAppModule) {
    const presetKey = flowId ?? presetMatchedKey;
    const presetConfig = presetStore.getState().getPreset(presetKey);
    DPISummaryWidgets = get(presetConfig, 'config.summaryWidgets', {});
  }
  const summaryWidgets = isAppModule ? DPISummaryWidgets : VSISummaryWidgets;

  useEffect(() => {
    if (flowId) {
      setSummaryPanel(true);
    }
  }, [flowId, setSummaryPanel]);

  // HP-7035: Switch off Summary Panel automatically on Service Performance Preset when time span is larger than 24 hours.
  useEffect(() => {
    if (summaryWidgets.type === SUMMARY_PANEL_TYPE.NETWORK) {
      const validInterval = isValidateIntervalForTreeMap(interval);
      if (!validInterval) {
        setSummaryPanel(false);
      } else {
        setSummaryPanel(true);
      }
    }
  }, [interval, setSummaryPanel, summaryWidgets.type]);

  // hide summary panel on compare mode
  const compareMode = useSelector(compareOnTrendsModeSelector);

  const isWidgetTabActive = useAtomValue(isWidgetTabActiveAtom);

  // hide summary panel under eco event preset dashboard.
  const renderCondition = {
    [SUMMARY_PANEL_TYPE.SPI]: isAppModule || isWidgetTabActive ? false : true,
    [SUMMARY_PANEL_TYPE.NETWORK]:
      isAppModule && usecaseKey === PRESET_KEYS.APP_NETWORK_PERFORMANCE ? true : false,
    [SUMMARY_PANEL_TYPE.FLOW]:
      isAppModule && !!flowId && enableTopBar && flowToolbarPanel ? true : false,
  };

  return showPanel && !compareMode ? (
    <ErrorBoundary>
      {renderWidgetPanelComponent({
        type: summaryWidgets.type,
        renderCondition,
        dispatch,
        flowId,
      })}
    </ErrorBoundary>
  ) : null;
};

export default WidgetPanelContainer;
